import PropTypes from 'prop-types';
import { SEARCH } from 'constants/filter-sets';
import { actionsForFilterSet, FILTER_WRITE_ACTIONS } from 'redux/modules/filtersets';
import { isLoggedIn } from 'redux/modules/user';
import { filterSetFilters, filterSetFilterValue } from 'redux/saga/selectors';
import { deepExtract } from 'helpers/deep';
import { isPresent } from 'helpers/verify';
import { defineMessages } from 'react-intl';
import { VERTICALS, FILTER_NAMES, CREDITS_FILTER_MIN_CREDITS, CREDITS_FILTER_MAX_CREDITS } from '../Search.constants';

export const searchFilterActions = actionsForFilterSet(SEARCH);

export const makeWriteSecondaryFilterSetSelector = filterSet => action =>
  FILTER_WRITE_ACTIONS.includes(action.type) &&
  deepExtract('data.filterSetId', action) === filterSet &&
  !deepExtract('data.options.excludeFromWriteActions', action) && // TODO - remove this in favor of a new action type that will be excluded from write actions
  deepExtract('data.filter', action) !== FILTER_NAMES.VERTICAL; // only want secondary filters here

export const selectSecondarySearchFilterWriteActions = makeWriteSecondaryFilterSetSelector(SEARCH);
export const selectSearchFilters = state => filterSetFilters(state, SEARCH);
export const selectSearchFilterValue = (state, filter) => filterSetFilterValue(state, SEARCH, filter);

export const initializeMultiSelectFromHash = (keys, filtersFromHash) =>
  // Due to the way multi-select filters are implemented in store, we can only include keys that are filtered
  keys.reduce((acc, amenityKey) => {
    if (filtersFromHash[amenityKey]) {
      if (!acc) {
        acc = {};
      }
      acc[amenityKey] = true;
    }
    return acc;
  }, null);

export const getCreditsFilterDisplayValues = creditsRange => [
  // Using isPresent to avoid unexpected behavior with 0
  creditsRange && isPresent(creditsRange.low) ? creditsRange.low : CREDITS_FILTER_MIN_CREDITS,
  creditsRange && isPresent(creditsRange.high) ? creditsRange.high : CREDITS_FILTER_MAX_CREDITS,
];

export const getCreditsFilterValue = (low, high) => {
  // Using isPresent to avoid unexpected behavior with 0
  // omit low value if less than or equal to the minimum
  const filterLowValue = isPresent(low) && low > CREDITS_FILTER_MIN_CREDITS ? low : null;
  // omit high value if greater than or equal to the maximum
  const filterHighValue = isPresent(high) && high < CREDITS_FILTER_MAX_CREDITS ? high : null;

  // return null if neither value is present to allow 'filtered' flag to be set appropriately in reducer
  return isPresent(filterLowValue) || isPresent(filterHighValue)
    ? {
        ...(isPresent(filterLowValue) && { low: filterLowValue }),
        ...(isPresent(filterHighValue) && { high: filterHighValue }),
      }
    : null;
};

export const getDefaultVerticalId = state => (isLoggedIn(state) ? VERTICALS.fitness : VERTICALS.all);

export const selectResultType = state => selectSearchFilterValue(state, FILTER_NAMES.RESULT_TYPE);

export const selectSelectedVertical = state =>
  selectSearchFilterValue(state, FILTER_NAMES.VERTICAL) || getDefaultVerticalId(state);

// return null if there is no selected range to allow 'filtered' flag to be set appropriately in reducer
export const selectSelectedCreditsRange = state => selectSearchFilterValue(state, FILTER_NAMES.CREDITS) || null;

export const creditsRangeShape = PropTypes.shape({
  low: PropTypes.number,
  high: PropTypes.number,
});

export const ALL_VERTICALS_OPTION = {
  id: VERTICALS.all,
  ...defineMessages({
    name: { id: 'filters.vertical.all.name', defaultMessage: 'All categories' },
  }),
};

export const BASE_VERTICALS_OPTIONS = [
  {
    id: VERTICALS.fitness,
    ...defineMessages({
      name: { id: 'filters.vertical.fitness.name', defaultMessage: 'Group Fitness' },
      description: { id: 'filters.vertical.fitness.description', defaultMessage: 'Yoga, boxing, pilates, etc' },
    }),
  },
  {
    id: VERTICALS.wellness,
    ...defineMessages({
      name: { id: 'filters.vertical.wellness.name', defaultMessage: 'Wellness' },
      description: { id: 'filters.vertical.wellness.description', defaultMessage: 'Massages, meditation, etc.' },
    }),
  },
  {
    id: VERTICALS.gym,
    ...defineMessages({
      name: { id: 'filters.vertical.gym.name', defaultMessage: 'Gym Time' },
      description: { id: 'filters.vertical.gym.description', defaultMessage: 'Book flexible sessions at the gym' },
    }),
  },
];

export const getVerticalOptions = includeAll =>
  includeAll ? [ALL_VERTICALS_OPTION].concat(BASE_VERTICALS_OPTIONS) : BASE_VERTICALS_OPTIONS;
